<template>
  <b-card no-body>
    <b-card-body class="pb-0">
      <div class="d-flex justify-content-between align-items-center border-bottom pb-1">
        <h4 class="text-body mb-0">
          {{ $t('subtasks.list.title') }}
        </h4>
        <div>
          <b-button
            v-if="canManage"
            variant="primary"
            size="sm"
            :disabled="locked"
            pill
            @click="openModal()"
          >
            <feather-icon icon="PlusIcon" />
            {{ $t('subtasks.list.add') }}
          </b-button>
        </div>
      </div>
    </b-card-body>
    <template v-if="loading">
      <b-card-body>
        <div class="text-center py-4">
          <b-spinner />
          <p class="mt-2 mb-0 text-muted">
            {{ $t('common.loading') }}
          </p>
        </div>
      </b-card-body>
    </template>
    <template v-else>
      <b-list-group
        flush
      >
        <b-list-group-item
          v-for="item in subtasksList"
          :key="item.id"
        >
          <b-row>
            <b-col>
              <b-row class="d-flex align-items-center">
                <b-col
                  cols="12"
                  md="4"
                  class="border-right"
                >
                  <div style="margin-bottom: 5px">
                    <div class="font-weight-bold text-primary font-medium-2">
                      {{ item.title }}
                    </div>
                    <div>
                      {{ item.description }}
                    </div>
                  </div>
                  <div style="margin-bottom: 5px">
                    <div
                      v-if="hasDates && (item.start_date || item.target_date)"
                      class="d-flex align-items-center font-small-3"
                    >
                      <div
                        v-if="item.start_date"
                        class="mr-1"
                      >
                        <strong>{{ $t('subtasks.list.startDate') }}</strong> {{ moment(item.start_date).format('DD.MM.YYYY') }}
                      </div>
                      <div v-if="item.target_date">
                        <strong>{{ $t('subtasks.list.targetDate') }}</strong> {{ moment(item.target_date).format('DD.MM.YYYY') }}
                      </div>
                    </div>
                  </div>
                  <div
                    class="d-flex align-items-center"
                    style="margin-bottom: 5px"
                  >
                    <div class="font-weight-bolder d-block mr-1">
                      {{ $t('subtasks.list.status') }}
                    </div>
                    <b-badge
                      :variant="subtaskStatus(item) === 'completed' ? 'success' : 'danger'"
                      pill
                    >
                      {{ statusLabel(item) }}
                    </b-badge>
                  </div>
                  <div>
                    <template v-if="item.timer_started_at">
                      <span class="font-weight-bolder">{{ $t('subtasks.list.timerStarted') }}</span> {{ moment(item.timer_started_at).format('DD.MM.YYYY') }}
                    </template>
                    <template v-if="item.completed_date">
                      /  <span class="font-weight-bolder">{{ $t('subtasks.list.ended') }}</span> {{ moment(item.completed_date).format('DD.MM.YYYY') }}
                    </template>
                  </div>
                </b-col>
                <b-col
                  class="d-flex align-items-center"
                >
                  <subtask-timer
                    v-if="hasTimer && item.id"
                    :subtask="item"
                    :read-only="subtaskStatus(item) !== 'open' || !canUseTimer(item)"
                    :callback="getDataList"
                  />
                </b-col>
              </b-row>
            </b-col>
            <b-col
              cols="12"
              md="auto"
              class="border-left d-flex align-items-center justify-content-end"
              style="min-width: 200px"
            >
              <div>
                <b-button
                  v-if="subtaskStatus(item) === 'completed'"
                  variant="outline-info"
                  pill
                  class="btn-icon ml-1"
                  @click="openCompletedModal(item)"
                >
                  <feather-icon icon="EyeIcon" />
                </b-button>
                <b-button
                  v-if="canManage"
                  variant="primary"
                  class="btn-icon ml-1"
                  :disabled="locked || subtaskStatus(item) !== 'open'"
                  pill
                  @click="openModal(item)"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>
                <b-button
                  v-if="canDelete(item)"
                  variant="danger"
                  pill
                  class="btn-icon ml-1"
                  @click="confirmRemove(item)"
                >
                  <feather-icon icon="TrashIcon" />
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-list-group-item>
      </b-list-group>
      <b-card-footer v-if="subtasksList.length === 0">
        <p class="text-muted mb-0 text-center">
          {{ $t('subtasks.list.empty') }}
        </p>
      </b-card-footer>
    </template>

    <completed-subtask-modal
      v-model="completedModalVisible"
      :subtask="selectedSubtask"
      :can-reopen="canManage && !locked"
      :refresh-data="getDataList"
      :show-toast="showToast"
    />

    <b-modal
      v-model="modalVisible"
      :title="isEdit ? $t('subtasks.list.editTitle') : $t('subtasks.list.newTitle')"
      size="lg"
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      no-enforce-focus
      centered
      @hidden="onModalHidden"
    >
      <subtask-form
        ref="subtaskForm"
        :id-tasks="idTasks"
        :initial-data="editItem"
        :read-only="readOnlyForm"
        :has-dates="hasDates"
        :has-timer-options="hasTimer"
        @saved="onSaved"
        @cancelled="modalVisible = false"
      />
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard, BCardBody, BBadge, BCardFooter, BRow, BCol, BButton, BListGroup, BListGroupItem, BSpinner,
} from 'bootstrap-vue'
import { canManageTask, canUseSubtaskTimer, isTaskManager } from '@/auth/taskPermissions'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { definitionLabel, statusSlug } from '@/libs/definitions'
import SubtaskForm from './SubtaskForm.vue'
import SubtaskTimer from './SubtaskTimer.vue'
import CompletedSubtaskModal from './CompletedSubtaskModal.vue'

export default {
  name: 'SubtasksList',
  components: {
    BCard,
    BCardBody,
    BCardFooter,
    BBadge,
    BRow,
    BCol,
    BButton,
    BListGroup,
    BListGroupItem,
    BSpinner,
    SubtaskForm,
    SubtaskTimer,
    CompletedSubtaskModal,
  },
  props: {
    idTasks: {
      type: Number,
      required: true,
    },
    locked: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      modalVisible: false,
      editItem: null,
      completedModalVisible: false,
      selectedSubtask: null,
    }
  },
  computed: {
    subtasksList() {
      return this.$store.getters['subtasks/dataList'] || []
    },
    loading() {
      return this.$store.getters['subtasks/loading']
    },
    taskItem() {
      return this.$store.getters['tasks/dataItem']
    },
    canManage() {
      return canManageTask(this.taskItem)
    },
    hasDates() {
      // return packageLimit('PROJECTS', PKG_STANDARD)
      return true
    },
    hasTimer() {
      // return packageLimit('PROJECTS', PKG_PREMIUM)
      return true
    },
    isEdit() {
      return !!this.editItem?.id
    },
    readOnlyForm() {
      return this.editItem?.close_status && this.editItem.close_status !== 'open'
    },
  },
  created() {
    this.getDataList()
  },
  methods: {
    getDataList() {
      this.$store.dispatch('subtasks/getDataList', {
        where: { 'subtasks.id_tasks': this.idTasks },
      })
      this.completedModalVisible = false
    },
    openModal(item = null) {
      this.editItem = item ? { ...item } : null
      this.modalVisible = true
      this.$nextTick(() => this.$refs.subtaskForm?.reset?.())
    },
    onModalHidden() {
      this.editItem = null
    },
    onSaved() {
      this.modalVisible = false
      this.getDataList()
      this.showToast({
        variant: 'success', title: this.$t('common.toast.successShort'), icon: 'CheckIcon', text: this.$t('subtasks.list.saved'),
      })
    },
    openCompletedModal(item) {
      this.selectedSubtask = item
      this.completedModalVisible = true
    },
    statusLabel(item) {
      return definitionLabel('subtask', item.subtask_status_slug, item.subtask_status || '-')
    },
    subtaskStatus(item) {
      return statusSlug('subtask', item)
    },
    canUseTimer(item) {
      return canUseSubtaskTimer(this.taskItem, item)
    },
    canDelete() {
      return isTaskManager(this.taskItem)
    },
    confirmRemove(item) {
      this.$swal({
        title: this.$t('alerts.remove.title'),
        text: this.$t('alerts.remove.message'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('alerts.remove.confirm'),
        cancelButtonText: this.$t('alerts.remove.cancel'),
        customClass: { confirmButton: 'btn btn-success', cancelButton: 'btn btn-danger ml-1' },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.$store.dispatch('subtasks/removeData', item.id).then(res => {
            if (res && res.status === false) {
              this.showToast({
                variant: 'danger', title: this.$t('common.toast.errorShort'), icon: 'XIcon', text: res.message || this.$t('subtasks.list.deleteFailed'),
              })
              return
            }
            this.getDataList()
            this.showToast({
              variant: 'success', title: this.$t('subtasks.list.deleted'), icon: 'CheckIcon', text: '',
            })
          })
        }
      })
    },
    showToast(params) {
      this.$toast({ component: ToastificationContent, props: params })
    },
  },
}
</script>

<style scoped>
.subtask-row {
  min-height: 56px;
}
</style>
